import { Component, EventEmitter, Input, Output, ViewChild, inject, OnInit, AfterViewInit, OnChanges, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import {
  DetailDataBoundEventArgs,
  FilterSettingsModel,
  GridComponent,
  PageSettingsModel,
} from '@syncfusion/ej2-angular-grids';
import { UtilsService } from '../../services/utils/utils.service';
import { DataUtil } from '@syncfusion/ej2-data';
import { Subscription } from 'rxjs';
import { WorkOrderStatus } from '../../enums/workOrder';
import { UserPreferencesService } from '../../services/user-preferences/user-preferences.service';
import { UntypedFormControl } from '@angular/forms';
import { WorkOrderService } from '../../services/work-order/work-order.service';

@Component({
  selector: 'app-syncfusion-hierarchy-table',
  // eslint-disable-next-line @angular-eslint/prefer-standalone -- NgModule-declared; standalone migration is separate work
  standalone: false,

  templateUrl: './syncfusion-hierarchy-table.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './syncfusion-hierarchy-table.component.scss',
})
export class SyncfusionHierarchyTableComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  private utilsService = inject(UtilsService);
  private userPreferencesService = inject(UserPreferencesService);
  private workOrderService = inject(WorkOrderService);

  @ViewChild('grid') grid: GridComponent;
  /* eslint-disable @typescript-eslint/no-explicit-any -- pre-existing untyped grid data/config inputs */
  @Input() tableData: any[] = [];
  @Input() childGrid: any = [];
  @Input() columnConfig: any[] = [];
  /* eslint-enable @typescript-eslint/no-explicit-any -- re-enable after the untyped grid block above */
  @Input() height = '400';

  // General grid options with default values
  @Input() allowReordering = true;
  @Input() allowSorting = true;
  @Input() allowResizing = true;
  @Input() allowPaging = true; // Default to allow paging
  @Input() pageSettings: PageSettingsModel = {
    pageSizes: ['25', '100', '200', '250'],
    pageCount: 4,
    pageSize: 25,
    currentPage: 1,
    totalRecordsCount: 25,
  };
  @Input() selectionOptions = { checkboxOnly: true }; // Remove checkbox toggle on rowClick
  @Input() tableType: string;
  /* eslint-disable @typescript-eslint/no-explicit-any -- pre-existing untyped grid event payloads */
  @Output() recordClick = new EventEmitter<any>();
  @Output() deleteAction = new EventEmitter<any>();
  @Output() beginRowEditEvent = new EventEmitter<any>();
  /* eslint-enable @typescript-eslint/no-explicit-any -- re-enable after the untyped grid block above */
  @Output() gridInstance: EventEmitter<GridComponent> =
    new EventEmitter<GridComponent>();
  @Output() childExpandEvent = new EventEmitter();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing untyped grid event payload
  @Output() parentRowDataBound = new EventEmitter<any>();

   // Filtering, grouping, and paging options with default values
  @Input() filterSettings: FilterSettingsModel = {
    type: 'CheckBox',
    operators: {
      dateOperator: [
        { value: 'equal', text: 'Equal' },
        { value: 'greaterThan', text: 'Greater Than' },
        { value: 'greaterThanOrEqual', text: 'Greater Than Or Equal' },
        { value: 'lessThan', text: 'Less Than' },
        { value: 'lessThanOrEqual', text: 'Less Than Or Equal' },
        { value: 'notEqual', text: 'Not Equal' },
      ],
    },
  };

  public loadingIndicator;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing untyped preferences blob
  userPreferencesData: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing untyped row id holder
  onendMenuRow: any;
  columnFilterChekSelectedOptions = {};
  searchControl = new UntypedFormControl('');
  subs = new Subscription();
  /* eslint-disable @typescript-eslint/no-explicit-any -- pre-existing untyped grid rows/status list/selection payloads */
  selectedRows: any[] = [];
  workOrderStatusList: any = [];
  @Output() rowSelectionEvent = new EventEmitter<any>();
  /* eslint-enable @typescript-eslint/no-explicit-any -- re-enable after the untyped grid block above */
  WorkOrderStatus = WorkOrderStatus;

  ngOnInit(): void {
    this.loadingIndicator = { indicatorType: 'Shimmer' };
    this.loadPreferenceData();
    this.refreshCompanyAndProjectDetails();
    this.workOrderStatusList = this.workOrderService.getWorkOrderStatusList() || [];
  }

  ngAfterViewInit() {
    if (this.grid) {
      this.gridInstance.emit(this.grid);
    }
  }

  loadPreferenceData() {
    this.userPreferencesData =
      this.userPreferencesService.getUserPreferencesData();
  }

  refreshCompanyAndProjectDetails() {
    // subscription for refresh whole project details
    this.subs.add(
      this.utilsService.refreshCompanyAndProjectDetails.subscribe((data) => {
        if (data) {
          this.loadPreferenceData();
        }
      })
    );
  }

  ngOnChanges() {
    this.tableData = DataUtil.parse.parseJson(this.tableData);
  }

  dataBound() {
    /* eslint-disable @typescript-eslint/no-explicit-any -- pre-existing Syncfusion internal addOnPersist patch */
    const cloned = (this.grid as any).addOnPersist;
    (this.grid as any).addOnPersist = function (key: any) {
      key = key.filter((item: string) => item !== 'searchSettings');
      return cloned.call(this, key);
    };
    /* eslint-enable @typescript-eslint/no-explicit-any -- re-enable after the untyped grid block above */
  }

  // Helper function for dynamic classes
  getStatusClass(statusIdOrName: string): string {
    if (!statusIdOrName) return '';
    const status = this.workOrderStatusList.find(
      (status) =>
        status?.id === statusIdOrName ||
        status?.name === statusIdOrName
    );
    // the status label's colour: the stored colorCode as it is (redesign checkpoint 2)
    return status?.colorCode || '';
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing untyped grid row payload
  onDelete(data: any) {
    this.deleteAction.emit(data); // Emit the data for the delete action
  }

  // Record click event handler to emit selected row data
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing untyped Syncfusion event args
  onRecordClick(event: any) {
    this.recordClick.emit(event); // Emit the clicked row data to the parent
  }

  // Example of triggering the dialog on a button click or other action
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing untyped Syncfusion selection event
  onRowSelection(event: any) {
    if (event?.name === 'rowSelected') {
      // ALWAYS produce a new array reference: parents pass selectedRows into
      // child @Inputs (e.g. the Download menu), which only re-fire on
      // reference change. Mutating in place left the menu blind to any
      // selection made after a deselect (select -> unselect -> select bug,
      // Vipin 2026-07-19). Dedupe by id so re-selecting a row can't double it.
      const added = event?.data?.length ? event.data : [event?.data];
      const addedIds = new Set(added.map((r) => r?.id));
      this.selectedRows = [
        ...this.selectedRows.filter((row) => !addedIds.has(row?.id)),
        ...added,
      ];
    } else {
      if (event?.data?.length) {
        this.selectedRows = [];
      } else {
        this.selectedRows = this.selectedRows.filter(
          (row) => row?.id !== event?.data?.id
        );
      }
    }
    this.rowSelectionEvent.emit(this.selectedRows);
  }

  isStatusWithPercentage(fieldValue: string): boolean {
    return (
      fieldValue !== WorkOrderStatus.New &&
      fieldValue !== WorkOrderStatus.Complete
    );
  }

  formatDate(dateString) {
    return this.utilsService.formatToReadableDate(dateString);
  }

  // Defense in depth: data[col.field] should already be a valid Date (or empty) by the time it
  // reaches here, but the `date` pipe throws NG02100 on an Invalid Date instance, so guard display.
  isValidDateValue(value: unknown): boolean {
    if (!value) return false;
    const d = value instanceof Date ? value : new Date(value as string);
    return !isNaN(d.getTime());
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing untyped grid row payload
  openActionMenu(data: any) {
    this.onendMenuRow = data.id;
  }

  onActionMenuClose() {
    this.onendMenuRow = null;
  }

  ngOnDestroy() {
    this.utilsService.refreshCompanyAndProjectDetails.next(false);
    this.subs.unsubscribe();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing untyped Syncfusion event args
  rowDataBound(args: any) {
    this.parentRowDataBound.emit(args);
  }

  detailDataBound({ data, childGrid }: DetailDataBoundEventArgs) {
    this.childExpandEvent.emit({ data, childGrid });
  }

  hasEditAction() {
    const actionColumn = this.columnConfig.filter((x) => x['isAction'] == true);
    if (actionColumn.length > 0) {
      return actionColumn[0].actions.some(
        (x) => x.buttonCaption?.toLowerCase() == 'edit'
      );
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing untyped grid row payload
  onEdit(data: any) {
    console.log('data: ', data);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing untyped grid action payload
  onActionClick(actionButton: any, data: any) {
    console.log('actionButton: ', actionButton);
    console.log('data: ', data);
  }
}
